import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Minus } from "lucide-react";
import FadeInUp from "./FadeInUp";
import { pricingPlans } from "./landingData";
import { getPlanPricing } from "../../lib/pricing";

// Countries with a localized pricing grid (same as the registration wizard).
const SUPPORTED_COUNTRIES = ["TN", "FR", "MA", "QA", "SA"];

// Timezone → country fallback when the IP lookup is unavailable.
const TZ_COUNTRY = {
  "Africa/Tunis": "TN",
  "Europe/Paris": "FR",
  "Africa/Casablanca": "MA",
  "Africa/El_Aaiun": "MA",
  "Asia/Qatar": "QA",
  "Asia/Bahrain": "QA",
  "Asia/Riyadh": "SA",
};

// Auto-detect the visitor's country (no manual selector). Primary source is the
// Cloudflare edge (/cdn-cgi/trace → loc=XX, IP-based); falls back to the browser
// timezone. Visitors outside the 5 localized countries — and any we can't place —
// default to EUR (the France grid), the international default.
const DEFAULT_COUNTRY = "FR"; // EUR
async function detectCountry() {
  try {
    const res = await fetch("/cdn-cgi/trace", { cache: "no-store" });
    const text = await res.text();
    const loc = (text.match(/^loc=([A-Z]{2})/m) || [])[1];
    if (loc) return SUPPORTED_COUNTRIES.includes(loc) ? loc : DEFAULT_COUNTRY;
  } catch (_) { /* ignore — fall back to timezone */ }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
  } catch (_) { /* ignore */ }
  return DEFAULT_COUNTRY;
}

const TABLE_SECTIONS = [
  {
    label: { fr: "FONCTIONNALITÉS DE BASE", en: "CORE FEATURES", ar: "الميزات الأساسية" },
    rows: [
      { label: { fr: "Gestion des employés",  en: "Employee management",  ar: "إدارة الموظفين"   }, checks: "all"     },
      { label: { fr: "Pointage & Présence",   en: "Attendance tracking",  ar: "تتبع الحضور"      }, checks: "all"     },
      { label: { fr: "Gestion des congés",    en: "Leave management",     ar: "إدارة الإجازات"   }, checks: "all"     },
      { label: { fr: "Rapports de base",      en: "Basic reports",        ar: "تقارير أساسية"    }, checks: "all"     },
      { label: { fr: "Organigramme",          en: "Org chart",            ar: "الهيكل التنظيمي"  }, checks: "pro-ent" },
    ],
  },
  {
    label: { fr: "RECRUTEMENT & CYCLE DE VIE", en: "HIRING & LIFECYCLE", ar: "التوظيف ودورة الحياة" },
    rows: [
      { label: { fr: "Offres d'emploi & pipeline ATS", en: "Job postings & ATS pipeline", ar: "إعلانات الوظائف والتتبع" }, checks: "pro-ent" },
      { label: { fr: "Portail carrières public",       en: "Public careers portal",       ar: "بوابة التوظيف العامة"   }, checks: "pro-ent" },
      { label: { fr: "Onboarding & Offboarding",        en: "Onboarding & Offboarding",    ar: "الإلحاق وإنهاء الخدمة"  }, checks: "pro-ent" },
      { label: { fr: "Contrats & E-signature",          en: "Contracts & E-signature",     ar: "العقود والتوقيع الإلكتروني" }, checks: "pro-ent" },
    ],
  },
  {
    label: { fr: "TALENT & DÉVELOPPEMENT", en: "TALENT & DEVELOPMENT", ar: "المواهب والتطوير" },
    rows: [
      { label: { fr: "Évaluations de performance & OKR", en: "Performance reviews & OKRs", ar: "تقييمات الأداء والأهداف" }, checks: "pro-ent" },
      { label: { fr: "Formation & LMS",                  en: "Learning & LMS",             ar: "التدريب ونظام التعلم"   }, checks: "pro-ent" },
      { label: { fr: "Engagement & Reconnaissance",      en: "Engagement & Recognition",   ar: "المشاركة والتقدير"      }, checks: "pro-ent" },
      { label: { fr: "Gestion de la succession",         en: "Succession planning",        ar: "تخطيط الخلافة"          }, checks: "ent"     },
    ],
  },
  {
    label: { fr: "RÉMUNÉRATION & DÉPENSES", en: "COMPENSATION & EXPENSES", ar: "التعويضات والنفقات" },
    rows: [
      { label: { fr: "Grilles de rémunération",  en: "Compensation bands",   ar: "سلالم الأجور"     }, checks: "pro-ent" },
      { label: { fr: "Avantages sociaux",        en: "Benefits administration", ar: "إدارة المزايا" }, checks: "pro-ent" },
      { label: { fr: "Notes de frais",           en: "Expense claims",       ar: "مطالبات النفقات"  }, checks: "pro-ent" },
    ],
  },
  {
    label: { fr: "FONCTIONNALITÉS IA", en: "AI FEATURES", ar: "ميزات الذكاء الاصطناعي" },
    rows: [
      { label: { fr: "IA Paie (détection d'erreurs)",  en: "AI Payroll (error detection)", ar: "رواتب AI"       }, checks: "pro-ent" },
      { label: { fr: "IA Recrutement (matching)",      en: "AI Recruitment (matching)",    ar: "توظيف AI"      }, checks: "pro-ent" },
      { label: { fr: "Assistant RH conversationnel",   en: "Conversational HR assistant",  ar: "مساعد الموارد البشرية" }, checks: "pro-ent" },
      { label: { fr: "Agents IA autonomes",            en: "Autonomous AI agents",         ar: "وكلاء IA مستقلون" }, checks: "ent"     },
    ],
  },
  {
    label: { fr: "CONFORMITÉ & SÉCURITÉ", en: "COMPLIANCE & SECURITY", ar: "الامتثال والأمان" },
    rows: [
      { label: { fr: "Conformité paie multi-pays", en: "Multi-country payroll compliance", ar: "امتثال الرواتب متعدد الدول" }, checks: "all"     },
      { label: { fr: "Gestion RGPD & piste d'audit", en: "GDPR & audit trail",             ar: "حماية البيانات وسجل التدقيق" }, checks: "pro-ent" },
      { label: { fr: "Déclarations sociales (DSN)",  en: "Social declarations (DSN)",       ar: "الإقرارات الاجتماعية"   }, checks: "ent"     },
    ],
  },
  {
    label: { fr: "INTÉGRATIONS", en: "INTEGRATIONS", ar: "التكاملات" },
    rows: [
      { label: { fr: "ZKTeco (terminaux)",             en: "ZKTeco (terminals)",     ar: "ZKTeco"                }, checks: "all"     },
      { label: { fr: "Export Excel/PDF",               en: "Excel/PDF Export",       ar: "تصدير Excel/PDF"       }, checks: "all"     },
      { label: { fr: "API & Webhooks",                 en: "API & Webhooks",         ar: "API والـ Webhooks"      }, checks: "pro-ent" },
      { label: { fr: "Intégrations personnalisées",    en: "Custom integrations",    ar: "تكاملات مخصصة"         }, checks: "ent"     },
    ],
  },
  {
    label: { fr: "SUPPORT & FORMATION", en: "SUPPORT & TRAINING", ar: "الدعم والتدريب" },
    rows: [
      { label: { fr: "Support email",                    en: "Email support",              ar: "دعم البريد"         }, checks: "all"     },
      { label: { fr: "Support prioritaire",              en: "Priority support",           ar: "دعم ذو أولوية"      }, checks: "pro-ent" },
      { label: { fr: "Formation incluse",                en: "Training included",          ar: "تدريب مشمول"        }, checks: "pro-ent" },
      { label: { fr: "Support 24/7",                    en: "24/7 Support",               ar: "دعم 24/7"           }, checks: "ent"     },
      { label: { fr: "Gestionnaire de compte dédié",    en: "Dedicated account manager",  ar: "مدير حساب مخصص"     }, checks: "ent"     },
    ],
  },
];

const CheckCell = ({ yes, isPro }) => (
  <td
    className="text-center"
    style={{
      padding: "12px 16px",
      backgroundColor: isPro ? "rgba(94,106,210,0.04)" : "transparent",
    }}
  >
    {yes ? (
      <Check
        className="mx-auto"
        style={{ width: "15px", height: "15px", color: "#27a644" }}
      />
    ) : (
      <Minus
        className="mx-auto"
        style={{ width: "14px", height: "14px", color: "#34343a" }}
      />
    )}
  </td>
);

const LandingPricing = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const t = (obj) => obj[i18n.language] || obj.fr;
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  // Auto-detected from the visitor's location; TN is the prerender/default.
  const [country, setCountry] = useState("TN");

  useEffect(() => {
    let alive = true;
    detectCountry().then((c) => { if (alive && c) setCountry(c); });
    return () => { alive = false; };
  }, []);

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <FadeInUp>
          <div className="text-center mb-12 space-y-4">
            <span
              className="inline-block text-xs font-medium uppercase tracking-widest px-3 py-1 rounded-full"
              style={{ color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.1)", border: "1px solid rgba(94,106,210,0.2)" }}
            >
              {t({ fr: "Tarifs", en: "Pricing", ar: "الأسعار" })}
            </span>
            <h2
              className="font-semibold"
              style={{ color: "#f7f8f8", fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-1px" }}
            >
              {t({ fr: "Des Plans pour Toutes les Tailles", en: "Plans for All Sizes", ar: "خطط لجميع الأحجام" })}
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: "#8a8f98" }}>
              {t({
                fr: "Choisissez le plan qui correspond à vos besoins",
                en: "Choose the plan that fits your needs",
                ar: "اختر الخطة التي تناسب احتياجاتك",
              })}
            </p>

            {/* Billing toggle */}
            <div
              className="inline-flex items-center p-1 rounded-full mt-4"
              style={{ backgroundColor: "#0f1011", border: "1px solid #23252a" }}
            >
              {[
                { key: "monthly", fr: "Mensuel", en: "Monthly", ar: "شهري" },
                { key: "annual",  fr: "Annuel",  en: "Annual",  ar: "سنوي"  },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSelectedPlan(opt.key)}
                  className="px-5 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2"
                  style={{
                    backgroundColor: selectedPlan === opt.key ? "#141516" : "transparent",
                    color: selectedPlan === opt.key ? "#f7f8f8" : "#8a8f98",
                    border: selectedPlan === opt.key ? "1px solid #34343a" : "1px solid transparent",
                  }}
                >
                  {t(opt)}
                  {opt.key === "annual" && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(39,166,68,0.15)", color: "#27a644" }}>
                      -17%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </FadeInUp>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {pricingPlans.map((plan, index) => {
            const pricing = getPlanPricing(plan.code, country);
            // Per-month figure: monthly as-is, annual = total/12 (2 months free).
            const perMonth = selectedPlan === "annual"
              ? Math.round(pricing.annual / 12)
              : pricing.monthly;
            return (
            <FadeInUp key={index} delay={index * 0.08}>
              <div
                className="relative p-6 rounded-xl flex flex-col h-full"
                style={{
                  backgroundColor: plan.popular ? "#141516" : "#0f1011",
                  border: plan.popular ? "1px solid rgba(94,106,210,0.5)" : "1px solid #23252a",
                  boxShadow: plan.popular ? "0 0 0 1px rgba(94,106,210,0.1), 0 8px 32px rgba(94,106,210,0.08)" : "none",
                }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className="text-xs font-medium px-3 py-1 rounded-full"
                      style={{ backgroundColor: "#5e6ad2", color: "#ffffff", boxShadow: "0 0 12px rgba(94,106,210,0.4)" }}
                    >
                      {t({ fr: "Plus Populaire", en: "Most Popular", ar: "الأكثر شعبية" })}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-semibold mb-1 text-base" style={{ color: "#f7f8f8" }}>{t(plan.name)}</h3>
                  <p className="text-xs mb-4" style={{ color: "#8a8f98" }}>{t(plan.description)}</p>

                  <>
                    <div className="text-3xl font-bold" style={{ color: "#f7f8f8", letterSpacing: "-1px" }}>
                      {perMonth}
                      <span className="text-sm font-normal ml-1" style={{ color: "#8a8f98" }}>
                        {pricing.symbol} {t({ fr: "/ mois", en: "/ mo", ar: "/ شهر" })}
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "#62666d" }}>
                      {selectedPlan === "annual"
                        ? t({ fr: "facturé annuellement", en: "billed annually", ar: "يُفوتر سنويًا" })
                        : t({ fr: "facturé mensuellement", en: "billed monthly", ar: "يُفوتر شهريًا" })}
                    </p>
                  </>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs" style={{ color: "#d0d6e0" }}>
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "#27a644" }} />
                      {t(feature)}
                    </li>
                  ))}
                </ul>

                <button
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-all"
                  style={
                    plan.popular
                      ? { backgroundColor: "#5e6ad2", color: "#ffffff", boxShadow: "0 0 16px rgba(94,106,210,0.25)" }
                      : { backgroundColor: "#18191a", color: "#f7f8f8", border: "1px solid #23252a" }
                  }
                  onClick={() => navigate("/register")}
                  onMouseEnter={(e) => {
                    if (plan.popular) {
                      e.currentTarget.style.backgroundColor = "#828fff";
                      e.currentTarget.style.boxShadow = "0 0 24px rgba(130,143,255,0.35)";
                    } else {
                      e.currentTarget.style.backgroundColor = "#23252a";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (plan.popular) {
                      e.currentTarget.style.backgroundColor = "#5e6ad2";
                      e.currentTarget.style.boxShadow = "0 0 16px rgba(94,106,210,0.25)";
                    } else {
                      e.currentTarget.style.backgroundColor = "#18191a";
                    }
                  }}
                >
                  {t(plan.cta)}
                </button>
              </div>
            </FadeInUp>
            );
          })}
        </div>

        {/* Feature comparison table */}
        <FadeInUp delay={0.3}>
          <div className="mt-20 max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="font-semibold mb-2" style={{ color: "#f7f8f8", fontSize: "24px", letterSpacing: "-0.5px" }}>
                {t({ fr: "Comparaison Détaillée des Fonctionnalités", en: "Detailed Feature Comparison", ar: "مقارنة تفصيلية للميزات" })}
              </h3>
              <p className="text-sm" style={{ color: "#8a8f98" }}>
                {t({ fr: "Trouvez le plan parfait pour votre entreprise", en: "Find the perfect plan for your business", ar: "اعثر على الخطة المثالية لعملك" })}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #23252a" }}>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  {/* Sticky header */}
                  <thead>
                    <tr style={{ backgroundColor: "#0f1011", borderBottom: "1px solid #23252a" }}>
                      <th className="text-left p-4 text-sm font-medium" style={{ color: "#8a8f98", width: "40%" }}>
                        {t({ fr: "Fonctionnalités", en: "Features", ar: "الميزات" })}
                      </th>
                      <th className="text-center p-4 text-sm font-medium" style={{ color: "#8a8f98" }}>
                        Starter
                        <div className="text-xs font-normal mt-0.5" style={{ color: "#62666d" }}>
                          {t({ fr: "jusqu'à 50 emp.", en: "up to 50 emp.", ar: "حتى 50 موظف" })}
                        </div>
                      </th>
                      <th
                        className="text-center p-4 text-sm font-medium"
                        style={{
                          color: "#828fff",
                          backgroundColor: "rgba(94,106,210,0.07)",
                          borderLeft: "1px solid rgba(94,106,210,0.2)",
                          borderRight: "1px solid rgba(94,106,210,0.2)",
                        }}
                      >
                        Professional
                        <div className="text-xs font-normal mt-0.5" style={{ color: "#5e6ad2" }}>
                          {t({ fr: "Recommandé", en: "Recommended", ar: "موصى به" })}
                        </div>
                      </th>
                      <th className="text-center p-4 text-sm font-medium" style={{ color: "#8a8f98" }}>
                        Enterprise
                        <div className="text-xs font-normal mt-0.5" style={{ color: "#62666d" }}>
                          {t({ fr: "Illimité", en: "Unlimited", ar: "غير محدود" })}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {TABLE_SECTIONS.map((section, si) => (
                      <Fragment key={`sec-${si}`}>
                        <tr style={{ backgroundColor: "#080809" }}>
                          <td
                            colSpan={4}
                            style={{
                              padding: "8px 16px",
                              fontSize: "10px",
                              fontWeight: 600,
                              letterSpacing: "0.6px",
                              textTransform: "uppercase",
                              color: "#62666d",
                              borderBottom: "1px solid #1a1b1e",
                            }}
                          >
                            {t(section.label)}
                          </td>
                        </tr>
                        {section.rows.map((row, ri) => {
                          const starter = row.checks === "all";
                          const pro     = row.checks === "all" || row.checks === "pro-ent";
                          const ent     = row.checks !== "none";
                          const isLast  = ri === section.rows.length - 1;
                          return (
                            <tr
                              key={`${si}-${ri}`}
                              style={{ borderBottom: isLast ? "none" : "1px solid #1a1b1e" }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.015)")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                            >
                              <td style={{ padding: "12px 16px", fontSize: "13px", color: "#d0d6e0" }}>
                                {t(row.label)}
                              </td>
                              <CheckCell yes={starter} isPro={false} />
                              <td
                                className="text-center"
                                style={{
                                  padding: "12px 16px",
                                  backgroundColor: "rgba(94,106,210,0.04)",
                                  borderLeft: "1px solid rgba(94,106,210,0.12)",
                                  borderRight: "1px solid rgba(94,106,210,0.12)",
                                }}
                              >
                                {pro ? (
                                  <Check className="mx-auto" style={{ width: "15px", height: "15px", color: "#27a644" }} />
                                ) : (
                                  <Minus className="mx-auto" style={{ width: "14px", height: "14px", color: "#34343a" }} />
                                )}
                              </td>
                              <CheckCell yes={ent} isPro={false} />
                            </tr>
                          );
                        })}
                        {/* Section spacer */}
                        {si < TABLE_SECTIONS.length - 1 && (
                          <tr key={`spacer-${si}`} style={{ borderBottom: "1px solid #23252a" }}>
                            <td colSpan={4} style={{ height: "1px", padding: 0 }} />
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div
                className="px-4 py-3 text-center"
                style={{ backgroundColor: "#0a0b0c", borderTop: "1px solid #23252a" }}
              >
                <p className="text-xs" style={{ color: "#62666d" }}>
                  {t({
                    fr: "Tous les plans incluent la conformité multi-pays EMEA, les mises à jour régulières et le chiffrement HTTPS/TLS",
                    en: "All plans include EMEA multi-country compliance, regular updates and HTTPS/TLS encryption",
                    ar: "تشمل جميع الخطط الامتثال متعدد الدول في EMEA والتحديثات المنتظمة وتشفير HTTPS/TLS",
                  })}
                </p>
              </div>
            </div>
          </div>
        </FadeInUp>
      </div>
    </section>
  );
};

export default LandingPricing;
